import React, { useEffect, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Tag,
  Tooltip,
  Empty,
  Popconfirm,
  Switch,
  Spin,
  Form,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import {
  selectEmailTemplateCategories,
  selectEmailTemplateCategoriesLoading,
} from "../../data/redux/email-templates/selectors";
import {
  createCategory,
  deleteCategory,
  fetchCategories,
  updateCategory,
} from "../../data/redux/email-templates/thunks";

import type { AppDispatch } from "../../data/redux/store";
import type { EmailTemplateCategory } from "../../data/redux/email-templates/types";
import { notify } from "../../../services/toast.config";

interface Props {
  open: boolean;
  onClose: () => void;
}

const COLOR_SUGGESTIONS = [
  "#3b82f6",
  "#10b981",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#22c55e",
  "#ec4899",
  "#6366f1",
  "#14b8a6",
  "#6b7280",
];

const ICON_SUGGESTIONS = [
  "📩",
  "🎯",
  "💼",
  "⚡",
  "🕒",
  "🌴",
  "💰",
  "📊",
  "✅",
  "🔐",
  "🛎️",
  "📣",
  "🎉",
];

const VALUE_PATTERN = /^[a-z][a-z0-9_]*$/;

const CategoryManagementModal: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const categories = useSelector(selectEmailTemplateCategories);
  const loading = useSelector(selectEmailTemplateCategoriesLoading);

  const [adding, setAdding] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newIcon, setNewIcon] = useState("📩");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [valueError, setValueError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editIcon, setEditIcon] = useState("");
  const [editColor, setEditColor] = useState("");

  useEffect(() => {
    if (open) {
      dispatch(fetchCategories());
      setAdding(false);
      setEditingId(null);
    }
  }, [open, dispatch]);

  const resetCreateState = () => {
    setNewValue("");
    setNewLabel("");
    setNewIcon("📩");
    setNewColor("#3b82f6");
    setValueError(null);
    setAdding(false);
  };

  const handleCreate = async () => {
    const value = newValue.trim().toLowerCase();
    if (!value) {
      setValueError("Category key is required");
      return;
    }
    if (!VALUE_PATTERN.test(value)) {
      setValueError(
        "Use lowercase letters, numbers, and underscores. Must start with a letter.",
      );
      return;
    }
    if (categories.some((c) => c.value === value)) {
      setValueError("This category already exists");
      return;
    }
    try {
      await dispatch(
        createCategory({
          value,
          label: newLabel.trim() || value,
          icon: newIcon,
          color: newColor,
        }),
      ).unwrap();
      notify.success("Category created");
      resetCreateState();
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to create category");
    }
  };

  const startEditing = (cat: EmailTemplateCategory) => {
    setEditingId(cat.id);
    setEditLabel(cat.label);
    setEditIcon(cat.icon || "");
    setEditColor(cat.color || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditLabel("");
    setEditIcon("");
    setEditColor("");
  };

  const saveEdit = async (cat: EmailTemplateCategory) => {
    try {
      await dispatch(
        updateCategory({
          categoryId: cat.id,
          payload: {
            label: editLabel.trim() || cat.label,
            icon: editIcon || undefined,
            color: editColor || undefined,
          },
        }),
      ).unwrap();
      notify.success("Category updated");
      cancelEditing();
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to update category");
    }
  };

  const handleToggleActive = async (cat: EmailTemplateCategory) => {
    try {
      await dispatch(
        updateCategory({
          categoryId: cat.id,
          payload: { is_active: !cat.is_active },
        }),
      ).unwrap();
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to update");
    }
  };

  const handleDelete = async (cat: EmailTemplateCategory) => {
    if (cat.is_default) {
      notify.warn("Default categories cannot be deleted.");
      return;
    }
    try {
      await dispatch(deleteCategory(cat.id)).unwrap();
      notify.success("Category deleted");
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to delete");
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontWeight: 600 }}>Manage Email Categories</span>
          <Tag style={{ fontSize: 11 }}>{categories.length}</Tag>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={
        <Button onClick={onClose} type="primary">
          Done
        </Button>
      }
      width={640}
      zIndex={1200}
      destroyOnClose
    >
      <p style={{ fontSize: 12, color: "#6b7280", marginTop: 0 }}>
        Categories help you group templates. Defaults can be deactivated but not
        deleted. Custom categories you add appear in the template editor and
        sample picker.
      </p>

      {adding ? (
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <Form layout="vertical" size="small">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
              }}
            >
              <Form.Item
                label="Category Key"
                required
                validateStatus={valueError ? "error" : undefined}
                help={valueError || "e.g. newsletter, alerts"}
                style={{ marginBottom: 8 }}
              >
                <Input
                  value={newValue}
                  onChange={(e) => {
                    setNewValue(e.target.value);
                    if (valueError) setValueError(null);
                  }}
                  placeholder="newsletter"
                />
              </Form.Item>
              <Form.Item
                label="Display Label"
                required
                style={{ marginBottom: 8 }}
              >
                <Input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="Newsletter"
                />
              </Form.Item>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
                  Icon
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {ICON_SUGGESTIONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setNewIcon(icon)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        border:
                          newIcon === icon
                            ? "2px solid #1677ff"
                            : "1px solid #e5e7eb",
                        background: newIcon === icon ? "#eef2ff" : "#fff",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                    >
                      {icon}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>
                  Color
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {COLOR_SUGGESTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewColor(color)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: color,
                        cursor: "pointer",
                        border:
                          newColor === color
                            ? "2px solid #111827"
                            : "1px solid #e5e7eb",
                        outline:
                          newColor === color
                            ? `2px solid ${color}33`
                            : "none",
                        outlineOffset: 1,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 10,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={resetCreateState}>Cancel</Button>
              <Button type="primary" onClick={handleCreate}>
                Create
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setAdding(true)}
            block
          >
            New category
          </Button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      ) : categories.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No categories yet"
        />
      ) : (
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {categories.map((cat, idx) => {
            const isEditing = editingId === cat.id;
            return (
              <div
                key={cat.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 14px",
                  borderBottom:
                    idx === categories.length - 1
                      ? "none"
                      : "1px solid #f0f0f0",
                  background: cat.is_active ? "#fff" : "#fafafa",
                  opacity: cat.is_active ? 1 : 0.7,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: `${cat.color || "#1677ff"}1a`,
                    color: cat.color || "#1677ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  {isEditing ? (editIcon || cat.icon || "📩") : (cat.icon || "📩")}
                </div>

                <div style={{ flex: 1 }}>
                  {isEditing ? (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Input
                        size="small"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        style={{ maxWidth: 200 }}
                      />
                      <div style={{ display: "flex", gap: 4 }}>
                        {ICON_SUGGESTIONS.slice(0, 6).map((icon) => (
                          <button
                            key={icon}
                            type="button"
                            onClick={() => setEditIcon(icon)}
                            style={{
                              width: 22,
                              height: 22,
                              borderRadius: 4,
                              border:
                                editIcon === icon
                                  ? "1px solid #1677ff"
                                  : "1px solid #e5e7eb",
                              background: editIcon === icon ? "#eef2ff" : "#fff",
                              cursor: "pointer",
                              fontSize: 11,
                            }}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                      <input
                        type="color"
                        value={editColor || cat.color || "#1677ff"}
                        onChange={(e) => setEditColor(e.target.value)}
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 4,
                          border: "1px solid #e5e7eb",
                          cursor: "pointer",
                          padding: 0,
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 13 }}>
                          {cat.label}
                        </span>
                        {cat.is_default && (
                          <Tag color="blue" style={{ fontSize: 10 }}>
                            Default
                          </Tag>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          fontFamily: "monospace",
                        }}
                      >
                        {cat.value}
                      </div>
                    </>
                  )}
                </div>

                {isEditing ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <Tooltip title="Save">
                      <Button
                        size="small"
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={() => saveEdit(cat)}
                      />
                    </Tooltip>
                    <Tooltip title="Cancel">
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={cancelEditing}
                      />
                    </Tooltip>
                  </div>
                ) : (
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                  >
                    <Tooltip title="Active">
                      <Switch
                        size="small"
                        checked={cat.is_active}
                        onChange={() => handleToggleActive(cat)}
                      />
                    </Tooltip>
                    <Tooltip title="Edit">
                      <Button
                        size="small"
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => startEditing(cat)}
                      />
                    </Tooltip>
                    <Tooltip title={cat.is_default ? "Default — cannot delete" : "Delete"}>
                      <Popconfirm
                        title="Delete this category?"
                        disabled={cat.is_default}
                        onConfirm={() => handleDelete(cat)}
                        okText="Delete"
                        cancelText="Cancel"
                      >
                        <Button
                          size="small"
                          type="text"
                          danger
                          disabled={cat.is_default}
                          icon={<DeleteOutlined />}
                        />
                      </Popconfirm>
                    </Tooltip>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default CategoryManagementModal;
